import Slider from "react-slick";

import useSearchProducts from "@whoppah/apollo/hooks/useSearchProducts";
import { useRouter } from "next/router";
import CarouselArrows from "../Homepage/CarouselArrows";
import HeroCarouselCss from "../Homepage/HeroCarousel/HeroCarousel.module.scss";
import ProductCard from "../product/ProductCard";
import { useCallback, useMemo } from "react";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { Heading } from "@/ui/cva/Heading";
import { cn } from "@/common/utils/css.utils";
import { lowerCase } from "lodash-es";

const ProductCarousel = ({ icon, name }) => {
  const router = useRouter();
  const { data, loading } = useSearchProducts({
    variables: {
      query: lowerCase(icon),
      pageSize: 10,
      lang: router.locale.toUpperCase(),
    },
  });

  const products = useMemo(
    () => data?.searchProducts?.items.filter(Boolean) || [],
    [data?.searchProducts?.items]
  );
  const searchMeta = {
    queryId: data?.searchProducts?.queryID ?? undefined,
    indexName: data?.searchProducts?.indexName ?? undefined,
    page: data?.searchProducts?.pagination?.page,
    limit: data?.searchProducts?.pagination?.limit,
  };

  /** Track `view_item_list` event. */
  analyticsService.useExecAfterMount(
    useCallback(() => {
      if (!loading) {
        analyticsService.trackViewItemList(
          "icon_products",
          "icon_products",
          products
        );
      }
    }, [products, loading])
  );

  if (loading || products.length === 0) {
    return null;
  }

  const NextArrow = ({ className, ...props }) => {
    return (
      <CarouselArrows.Right
        className={cn(className, "md:-right-2.5 xl:-right-4")}
        {...props}
      />
    );
  };

  const PrevArrow = ({ className, ...props }) => {
    return (
      <CarouselArrows.Left
        className={cn(className, "md:-left-2.5 xl:-left-4")}
        {...props}
      />
    );
  };

  return (
    <div>
      <Heading as="h4" className="mb-6 text-center">
        {name}
      </Heading>
      <div className="grid w-full grid-cols-2 gap-4 md:hidden">
        {products.slice(0, 4).map((product, index) => (
          <ProductCard
            key={product.id}
            item={product}
            searchMeta={searchMeta}
            listName={`icon-${icon}-list`}
            index={index}
          />
        ))}
      </div>
      <div className="hidden h-full md:block">
        {products.length <= 4 ? (
          <div className="flex space-x-4">
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                item={product}
                searchMeta={searchMeta}
                listName={`icon-${icon}-list`}
                index={index}
              />
            ))}
          </div>
        ) : (
          <Slider
            dots
            dotsClass={cn("slick-dots", HeroCarouselCss["carousel-dots"])}
            slidesToShow={Math.min(products.length, 4)}
            nextArrow={<NextArrow />}
            prevArrow={<PrevArrow />}
            className={HeroCarouselCss["carousel-full-height"]}
          >
            {products.map((product, index) => (
              <div key={product?.id} className="h-full w-full px-8 py-2">
                <ProductCard
                  key={product.id}
                  item={product}
                  searchMeta={searchMeta}
                  listName={`icon-${icon}-list`}
                  index={index}
                />
              </div>
            ))}
          </Slider>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel;
